import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import {
  getMaturityScoringBySubDomainId,
  updateMaturityScoringAction,
} from '../../redux/maturityScoring/maturityScoringActions'
import { MaturityScoring } from '../../types/maturityScoringTypes'
import ActionListBySubDomain from '../action/ActionListBySubDomains'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { useTranslation } from 'react-i18next'
import MaturityScoringModal from '../../components/containers/MaturityScoringModal'
import {
  selectDocument,
  selectDocumentLoading,
} from '../../redux/documents/documentSelector'
import UseAlert from '../../components/common/alert'
import { fetchDocumentById } from '../../redux/documents/documentAction'
import { formatTitle } from '../../utils/formatTitle'
import { formatStructuredDescription } from '../../utils/formatStructuredDescription'
import { getSubDomainById } from '../../redux/subDomains/subDomainActions'
import { selectSubDomain } from '../../redux/subDomains/subDomainSelectors'
import {
  DetailAccordion,
  DetailButton,
  DetailDashGrid,
  DetailInfoGrid,
  DetailInfoItem,
  DetailKpiCard,
  DetailKpiGrid,
  DetailPageHead,
  DetailPanel,
  DetailProse,
} from '../../components/common/detail'
import SupportDocumentPanel from '../action/SupportDocumentPanel'
import { AppDispatch, RootState, useAppDispatch } from '../../redux/store'
import '../../components/common/ListPageTable.css'
import { formatDecimal } from '../../utils/formatNumbers'

export default function MaturityDetails() {
  const [openSections, setOpenSections] = useState({
    proof: true,
    tasks: true,
  })
  const [Maturity, setMaturity] = useState<MaturityScoring>({
    _id: '',
    MaturityId: '',
    AnswerScore: 0,
    SubDomainScore: 0,
    SubDomainId: '',
    proof: null,
  })
  const location = useLocation()
  const navigate = useNavigate()
  const targetScoreFromState = location.state?.targetScore
  const dispatch: AppDispatch = useAppDispatch()
  const MaturityS = useSelector(
    (state: RootState) => state.maturityScoring.MaturityScoringBySubDomainId
  )
  const subDomain = useSelector(selectSubDomain)
  const { id } = useParams<{ id: string }>()
  const document = useSelector(selectDocument)
  const loadingDoc = useSelector(selectDocumentLoading)
  const { t } = useTranslation()
  const roles = useSelector((state: any) => state.auth.roles)
  const [open2, setOpen2] = useState(false)
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })
  const [file, setFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState('')
  const [savingProof, setSavingProof] = useState(false)

  const hasMaturityData = Boolean(Maturity?._id)
  const hasProofId = Boolean(Maturity?.proof)
  const canManage =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)
  const missingOrBroken =
    hasProofId && !loadingDoc && !document?.originalName

  const targetScore = useMemo(() => {
    const fromState = Number(targetScoreFromState)
    if (!Number.isNaN(fromState) && fromState >= 0) return fromState
    const fromSub = Number(subDomain?.TargetScore)
    if (!Number.isNaN(fromSub) && fromSub >= 0) return fromSub
    return null
  }, [targetScoreFromState, subDomain?.TargetScore])

  const formattedDescription = formatStructuredDescription(
    subDomain?.Description
  )
  const subdomainTitle = formatTitle(
    subDomain?.LabelSD || t('Subdomain', { defaultValue: 'Subdomain' })
  )
  const controlLabel = subDomain?.Label
    ? formatTitle(subDomain.Label)
    : '—'
  const source = subDomain?.Source || '—'
  const kpiColumns = canManage ? 3 : 2

  useEffect(() => {
    if (id) {
      dispatch(getMaturityScoringBySubDomainId(id))
      dispatch(getSubDomainById(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (MaturityS !== null) {
      setMaturity({
        _id: MaturityS._id,
        MaturityId: MaturityS.MaturityId,
        AnswerScore: MaturityS.AnswerScore,
        SubDomainScore: MaturityS.SubDomainScore,
        SubDomainId: MaturityS.SubDomainId,
        proof: MaturityS.proof,
      })
      setFileName(document?.originalName ?? '')
    }
  }, [MaturityS, document])

  useEffect(() => {
    if (Maturity.proof && typeof Maturity.proof === 'string') {
      dispatch(fetchDocumentById(Maturity.proof))
    }
  }, [dispatch, Maturity.proof])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0]
    if (next) {
      setFileName(next.name)
      setFile(next)
    }
  }

  const handleAnswerScoreChange = (value: number) =>
    setMaturity((prev) => ({ ...prev, AnswerScore: value }))

  const handleEditMaturity = async (finalScore?: number) => {
    if (!Maturity._id) return
    const answerScore =
      typeof finalScore === 'number' ? finalScore : Maturity.AnswerScore
    const formData = new FormData()
    formData.append('AnswerScore', String(answerScore))
    formData.append('SubDomainId', Maturity.SubDomainId)
    if (file) formData.append('proof', file)

    try {
      const result = await dispatch(
        updateMaturityScoringAction({ id: Maturity._id, formData })
      ).unwrap()
      setMaturity((prev) => ({ ...prev, ...result }))
      setFile(null)
      setOpen2(false)
      setAlert({
        message: t('alerts.onUpdate.success'),
        severity: 'success',
        open: true,
      })
      if (id) dispatch(getMaturityScoringBySubDomainId(id))
    } catch (err) {
      console.error('Update failed:', err)
      setAlert({
        message: t('alerts.onUpdate.error'),
        severity: 'error',
        open: true,
      })
    }
  }

  const handleSaveProof = async (nextFile: File) => {
    if (!Maturity._id) return
    setSavingProof(true)
    const formData = new FormData()
    formData.append('AnswerScore', String(Maturity.AnswerScore))
    formData.append('SubDomainId', Maturity.SubDomainId)
    formData.append('proof', nextFile)
    try {
      const result = await dispatch(
        updateMaturityScoringAction({ id: Maturity._id, formData })
      ).unwrap()
      setMaturity((prev) => ({ ...prev, ...result }))
      setFileName(nextFile.name)
      setAlert({
        message: t('alerts.onUpdate.success'),
        severity: 'success',
        open: true,
      })
      if (id) dispatch(getMaturityScoringBySubDomainId(id))
    } catch (err) {
      console.error(err)
      setAlert({
        message: t('alerts.onUpdate.error'),
        severity: 'error',
        open: true,
      })
    } finally {
      setSavingProof(false)
    }
  }

  const gapToTarget =
    hasMaturityData && targetScore != null
      ? formatDecimal((targetScore - Number(Maturity.AnswerScore)))
      : null

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
      />
      <div className="detail-page">
        <DetailPageHead
          eyebrow={t('maturityManagementPage.title')}
          title={formatTitle(t('maturityScoringPage.title'))}
          subtitle={t('maturityScoringPage.subtitle')}
          actions={
            <>
              <DetailButton variant="ghost" onClick={() => navigate(-1)}>
                {t('Back')}
              </DetailButton>
              {canManage ? (
                <DetailButton onClick={() => setOpen2(true)}>
                  <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                  {t('buttons.updateMaturity')}
                </DetailButton>
              ) : null}
            </>
          }
        />

        <DetailKpiGrid columns={kpiColumns as 2 | 3}>
          <DetailKpiCard
            label={t('maturityScoringPage.answerScore')}
            value={
              hasMaturityData
                ? formatDecimal(Number(Maturity.AnswerScore))
                : '—'
            }
            description={t('maturityScoringPage.answerScoreDesc')}
          />
          {canManage ? (
            <DetailKpiCard
              label={t('maturityScoringPage.subDomainScore')}
              value={
                hasMaturityData
                  ? formatDecimal(Number(Maturity.SubDomainScore))
                  : '—'
              }
              description={t('maturityScoringPage.subDomainScoreDesc')}
            />
          ) : null}
          <DetailKpiCard
            label={t('maturityScoringPage.proof')}
            value={hasProofId ? t('Yes') : t('No')}
            valueSize="sm"
            description={t('maturityScoringPage.proofDesc')}
          />
        </DetailKpiGrid>

        <DetailDashGrid>
          <DetailPanel
            className="detail-panel--sticky"
            title={t('maturityScoringPage.overviewTitle')}
            subtitle={t('maturityScoringPage.overviewSubtitle')}
          >
            {hasMaturityData || subDomain ? (
              <DetailInfoGrid variant="stack">
                <DetailInfoItem label={t('label')} value={subdomainTitle} />
                <DetailInfoItem label={t('Control')} value={controlLabel} />
                <DetailInfoItem label={t('Source')} value={source} />
                <DetailInfoItem
                  label={t('maturityManagementPage.tableMaturity.TargetScore')}
                  value={
                    targetScore != null ? String(targetScore) : '—'
                  }
                />
                {gapToTarget != null ? (
                  <DetailInfoItem
                    label={t('maturityScoringPage.gapToTarget')}
                    value={gapToTarget}
                  />
                ) : null}
                {id ? (
                  <DetailInfoItem
                    label={t('maturityScoringPage.subdomainLink')}
                    value={
                      <button
                        type="button"
                        className="detail-inline-link"
                        onClick={() => navigate(`/subDomain/${id}`)}
                      >
                        {t('maturityScoringPage.openSubdomain')}
                      </button>
                    }
                  />
                ) : null}
              </DetailInfoGrid>
            ) : (
              <p className="detail-prose-empty">
                {t('maturityScoringPage.noMaturityData')}
              </p>
            )}
          </DetailPanel>

          <DetailPanel
            className="detail-panel--prose"
            title={t('Description')}
            subtitle={t('maturityScoringPage.descriptionSubtitle')}
          >
            <DetailProse
              text={formattedDescription}
              emptyLabel={t('maturityScoringPage.noDescription')}
            />
          </DetailPanel>
        </DetailDashGrid>

        <DetailAccordion
          title={t('maturityScoringPage.proof')}
          open={openSections.proof}
          onToggle={() =>
            setOpenSections((s) => ({ ...s, proof: !s.proof }))
          }
        >
          <div className="detail-acc-inner">
            <SupportDocumentPanel
              hasSupportId={hasProofId}
              document={document}
              loading={loadingDoc}
              missingOrBroken={missingOrBroken}
              canManage={canManage && hasMaturityData}
              saving={savingProof}
              onSaveFile={handleSaveProof}
              attachedHint={t('maturityScoringPage.proofAttached')}
              emptyTitle={t('maturityScoringPage.noProof')}
              emptyHint={t('maturityScoringPage.proofUploadHint')}
              emptyReadonly={t('maturityScoringPage.noProof')}
            />
          </div>
        </DetailAccordion>

        <ActionListBySubDomain
          IdSubDomain={id || ''}
          open={openSections.tasks}
          onToggle={() =>
            setOpenSections((s) => ({ ...s, tasks: !s.tasks }))
          }
        />
      </div>

      <MaturityScoringModal
        open={open2}
        onClose={() => setOpen2(false)}
        title={t('popups.updateMaturityScoring.title')}
        maturityData={Maturity}
        onAnswerScoreChange={handleAnswerScoreChange}
        onEdit={handleEditMaturity}
        onFileChange={handleFileChange}
        fileName={fileName || document?.originalName}
        proofFile={file}
        subDomainId={Maturity.SubDomainId}
        existingDocument={document}
        mode="edit"
        t={t}
        TargetScore={targetScore ?? undefined}
      />
    </>
  )
}
